/**
 * Het micro-slot, los van React.
 *
 * Zie `docs/architecture/gelijktijdigheid.md` §5. Er zijn twee deuren naar
 * hetzelfde veld: het attributenpaneel en het hernoemen op het canvas zelf
 * (F2, dubbelklik). Eén ervan op slot doen beschermt niets — twee mensen
 * kunnen dan nog steeds dezelfde naam herschrijven zolang ze een andere deur
 * nemen. Daarom woont het claimen hier, waar beide kanten erbij kunnen, en
 * niet in de hook van het paneel.
 *
 * Per object wordt geteld hoeveel bewerkingen er lopen. Wie als laatste
 * afsluit geeft vrij; wie tussendoor van paneel naar canvas springt houdt z'n
 * slot. Zonder die telling zou de ene deur het slot van de andere opheffen.
 */
import { api } from "../api/client";
import { useSession } from "./session";

/** Verlengen terwijl iemand doortypt; ruim binnen de vervaltijd van de
 *  serverkant, zodat een trage verbinding niet meteen je slot kost. */
const HEARTBEAT_MS = 10_000;

export interface LockDenial {
  /** Wie het vasthoudt: een collega, of het id van een lopende operatie. */
  holder: string;
  /** Vastgehouden vanwege een import of restore in plaats van een collega. */
  byBatch: boolean;
}

interface Held {
  count: number;
  heartbeat: ReturnType<typeof setInterval>;
}

const held = new Map<string, Held>();

/**
 * Wie wil horen dat 'ie z'n slot kwijt is.
 *
 * De hartslag houdt het slot vast zolang je bezig bent, maar er zijn gevallen
 * waarin 'ie 'm tóch niet meer terugkrijgt: het netwerk viel lang genoeg weg,
 * of een import nam de scope over. Dan hoort de bewerker dat te weten vóór
 * 'ie z'n werk opslaat en geweigerd wordt — niet erna.
 */
type VerliesLuisteraar = (objectId: string) => void;
let opVerlies: VerliesLuisteraar | null = null;

export function onLockLost(luisteraar: VerliesLuisteraar | null): void {
  opVerlies = luisteraar;
}

function scope(): { workspaceId: string; branchId: string } | null {
  const { workspaceId, branchId } = useSession.getState();
  return workspaceId && branchId ? { workspaceId, branchId } : null;
}

/**
 * Probeert het object te claimen. Geeft `null` terug als het gelukt is, en
 * anders wie het vasthoudt.
 *
 * Is de laag onbereikbaar, dan lukt het: bescherming die zichzelf niet kan
 * bevestigen mag het werk niet stilleggen.
 */
export async function claimObjectLock(objectId: string): Promise<LockDenial | null> {
  const here = scope();
  if (!here) return null;
  // Alleen kijken: wie niets mag wijzigen claimt ook geen slot (de worker
  // weigert het, en het zou een schrijver blokkeren).
  if (useSession.getState().readOnly) return null;

  const existing = held.get(objectId);
  if (existing) {
    existing.count += 1;
    return null;
  }

  try {
    const res = await api.acquireObjectLocks(here.workspaceId, here.branchId, [objectId]);
    const denial = res.denied[0];
    if (denial) return { holder: denial.holder, byBatch: denial.reason === "scope" };
  } catch {
    return null;
  }

  // Tussen het verzoek en het antwoord kan een tweede bewerking begonnen zijn.
  const again = held.get(objectId);
  if (again) {
    again.count += 1;
    return null;
  }
  held.set(objectId, {
    count: 1,
    heartbeat: setInterval(() => {
      const now = scope();
      if (!now) return;
      void api
        .refreshObjectLocks(now.workspaceId, now.branchId, [objectId])
        .then((res) => {
          // Niets verlengd betekent: het slot is er niet meer. Verlopen
          // doordat we te lang niets konden sturen, of overgenomen door een
          // batch die de scope claimde.
          if (!res.refreshed.includes(objectId)) {
            releaseObjectLock(objectId);
            opVerlies?.(objectId);
          }
        })
        .catch(() => {
          // Onbereikbaar zegt niets over het slot; de volgende hartslag
          // probeert het opnieuw.
        });
    }, HEARTBEAT_MS),
  });
  return null;
}

/** Geeft één bewerking terug. Pas als de laatste klaar is gaat het slot eraf. */
export function releaseObjectLock(objectId: string): void {
  const entry = held.get(objectId);
  if (!entry) return;
  entry.count -= 1;
  if (entry.count > 0) return;
  clearInterval(entry.heartbeat);
  held.delete(objectId);
  const here = scope();
  if (!here) return;
  void api.releaseObjectLocks(here.workspaceId, here.branchId, [objectId]).catch(() => {});
}

/** Houden wíj dit object vast? Voor wie wil weten of 'ie al binnen is zonder
 *  te claimen. */
export function holdsObjectLock(objectId: string): boolean {
  return held.has(objectId);
}
